/**
 * EPD-2014: Sales-specific CTA options for Sales campaigns
 *
 * This array contains the 23 unique Call to Action options that are allowed for Sales campaigns
 * as specified in EPD-2014. This is used for both UI filtering and schema validation.
 *
 * The options are mapped from user-friendly names to actual Meta API values:
 * - Some have direct matches (e.g., "Apply now" -> APPLY_NOW)
 * - Some use closest available alternatives (e.g., "Check Availability" -> GET_IN_TOUCH)
 * - Some share the same API value (e.g., "Preregister" and "Sign up" both use SIGN_UP)
 */
export const SALES_CAMPAIGN_CTA_OPTIONS = [
  'NO_BUTTON',
  'APPLY_NOW',
  'BOOK_NOW',
  'CALL_NOW',
  'GET_IN_TOUCH', // Check Availability
  'CONTACT_US',
  'DONATE_NOW',
  'DOWNLOAD',
  'LISTEN_NOW',
  'GET_DIRECTIONS',
  'GET_OFFER',
  'GET_SHOWTIMES',
  'GET_QUOTE',
  'ORDER_NOW',
  'PLAY_GAME',
  'SIGN_UP', // Preregister & Sign up
  'REQUEST_TIME',
  'SEE_MENU',
  'INSTAGRAM_MESSAGE', // Send message
  'SHOP_NOW',
  'SUBSCRIBE',
  'CONFIRM', // Vote now
  'WATCH_MORE',
] as const;

export type SalesCampaignCtaOption = (typeof SALES_CAMPAIGN_CTA_OPTIONS)[number];

/**
 * Type guard function to safely check if a call to action type is allowed for Sales campaigns
 * @param callToActionType - The call to action type to check
 * @returns true if the call to action type is allowed for Sales campaigns
 */
export function isSalesCampaignCtaOption(callToActionType: string): callToActionType is SalesCampaignCtaOption {
  return SALES_CAMPAIGN_CTA_OPTIONS.includes(callToActionType as SalesCampaignCtaOption);
}
